import { useState, useCallback, useEffect, useRef } from "react";
import { useLogs } from "../context/LogsContext";

/**
 * Custom hook for handling answer logs search functionality
 * @returns {Object} Search state and handlers
 */
export const useAnswerLogsSearch = () => {
  const {
    pagination,
    fetchAnswersForPage,
    searchAnswers,
    generateCacheKey,
    queryCache,
  } = useLogs();

  // Search mode state
  const [searchMode, setSearchMode] = useState("answerId");
  const [answerIdQuery, setAnswerIdQuery] = useState("");
  const [emailQuery, setEmailQuery] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // For managing pagination
  const [originalPagination, setOriginalPagination] = useState(null);

  // Refs for tracking search state
  const currentSearchPageRef = useRef(1);
  const prevSearchQueryRef = useRef({ mode: "", query: "" });

  // Save original pagination when not searching
  useEffect(() => {
    if (!isSearching && pagination.total > 0) {
      setOriginalPagination(pagination);
    }
  }, [pagination, isSearching]);

  // Get current search query based on mode
  const getCurrentSearchQuery = useCallback(() => {
    switch (searchMode) {
      case "answerId":
        return answerIdQuery;
      case "email":
        return emailQuery;
      case "organization":
        return selectedOrgId;
      default:
        return "";
    }
  }, [searchMode, answerIdQuery, emailQuery, selectedOrgId]);

  // Execute search with appropriate parameters
  const executeSearch = useCallback(
    (mode, query, page, limit) => {
      if (!query.trim()) return;

      setIsSearching(true);

      // Generate different cache keys based on search mode
      const formattedQuery =
        mode === "organization" ? parseInt(query, 10) : query;
      const cacheKey = generateCacheKey(
        page,
        limit,
        `${mode}:${formattedQuery}`
      );

      const searchParams = {};

      if (mode === "answerId") {
        searchParams.search = query;
      } else if (mode === "email") {
        searchParams.email = query;
      } else if (mode === "organization") {
        searchParams.orgid = parseInt(query, 10);
      }

      // Pass a flag indicating whether the data is already cached
      searchAnswers(searchParams, page, limit, !!queryCache[cacheKey]);
    },
    [searchAnswers, generateCacheKey, queryCache]
  );

  // Handle search mode change
  const handleSearchModeChange = useCallback(
    (mode) => {
      setSearchMode(mode);
      setIsSearching(false);

      // Clear previous searches when changing modes
      if (mode !== "answerId") setAnswerIdQuery("");
      if (mode !== "email") setEmailQuery("");
      if (mode !== "organization") setSelectedOrgId("");

      // Restore to first page of non-search results
      fetchAnswersForPage(1);

      // Reset refs
      currentSearchPageRef.current = 1;
      prevSearchQueryRef.current = { mode: "", query: "" };
    },
    [fetchAnswersForPage]
  );

  // Handle answer ID search input change
  const handleAnswerIdChange = useCallback(
    (value) => {
      setAnswerIdQuery(value);

      if (!value.trim() && isSearching) {
        setIsSearching(false);
        fetchAnswersForPage(1);
        currentSearchPageRef.current = 1;
      }
    },
    [isSearching, fetchAnswersForPage]
  );

  // Handle email search input change
  const handleEmailChange = useCallback(
    (value) => {
      setEmailQuery(value);

      if (!value.trim() && isSearching) {
        setIsSearching(false);
        fetchAnswersForPage(1);
        currentSearchPageRef.current = 1;
      }
    },
    [isSearching, fetchAnswersForPage]
  );

  // Handle organization selection
  const handleOrgSelect = useCallback(
    (orgId) => {
      setSelectedOrgId(orgId);

      if (orgId) {
        // Convert orgId to number to ensure proper API call
        const numericOrgId = parseInt(orgId, 10);
        executeSearch(
          "organization",
          numericOrgId.toString(),
          1,
          pagination.limit
        );
      } else {
        setIsSearching(false);
        fetchAnswersForPage(1);
      }
    },
    [pagination.limit, executeSearch, fetchAnswersForPage]
  );

  // Execute debounced search for text inputs
  useEffect(() => {
    // Skip if we're in organization search mode
    if (searchMode === "organization") return;

    const currentQuery = getCurrentSearchQuery();
    const currentSearchState = { mode: searchMode, query: currentQuery };

    // Skip effect if the search query and mode haven't changed
    if (
      prevSearchQueryRef.current.mode === searchMode &&
      prevSearchQueryRef.current.query === currentQuery
    ) {
      return;
    }

    if (currentQuery.trim()) {
      // Reset to page 1 only when search query changes
      currentSearchPageRef.current = 1;

      const timeoutId = setTimeout(() => {
        executeSearch(searchMode, currentQuery, 1, pagination.limit);
        // Update the previous search state
        prevSearchQueryRef.current = currentSearchState;
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      // Update the previous search state when cleared
      prevSearchQueryRef.current = currentSearchState;
    }
  }, [
    answerIdQuery,
    emailQuery,
    searchMode,
    pagination.limit,
    executeSearch,
    getCurrentSearchQuery,
  ]);

  // Determine which pagination to use
  const effectivePagination = isSearching
    ? pagination
    : originalPagination && (!pagination.total || pagination.total === 0)
    ? originalPagination
    : pagination;

  return {
    searchMode,
    answerIdQuery,
    emailQuery,
    selectedOrgId,
    isSearching,
    currentSearchPageRef,
    effectivePagination,
    executeSearch,
    handleSearchModeChange,
    handleAnswerIdChange,
    handleEmailChange,
    handleOrgSelect,
    getCurrentSearchQuery,
  };
};
